import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Heart, Loader2, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { inputCls } from "@/components/kit";
import { Card, PublicShell, Stars, useLang } from "@/components/PublicShell";
import { api, errMsg } from "@/lib/api";

const Q = [["cibo", "Cucina e menù", "Food & menu"], ["servizio", "Servizio in sala", "Service"], ["location", "Location e allestimento", "Venue & decor"], ["organizzazione", "Organizzazione", "Organisation"]];

function Thanks({ r, t }) {
  const link = `${window.location.origin}/richiesta?fonte=Passaparola&amico=${r.referral_code}`;
  return (
    <div className="space-y-5 text-center" data-testid="survey-done">
      <Heart size={40} className="mx-auto text-[#E07A5F]" />
      <h1 className="font-serif-it text-3xl font-semibold">{t("Grazie di cuore!", "Thank you so much!")}</h1>
      {r.reviews?.length > 0 && (
        <div className="space-y-2">
          <p className="text-[#47606B]">{t("Ci fa tanto piacere. Ci regaleresti una recensione? Aiuta altre coppie e famiglie a sceglierci. ✨", "We're so glad! Would you leave us a review? It helps other couples and families choose us. ✨")}</p>
          <div className="flex flex-col gap-2">{r.reviews.map((x, i) => <a key={i} href={x.url} target="_blank" rel="noreferrer" className="rounded-full bg-[#0B6E8F] text-white py-3 font-medium hover:bg-[#0D2B3A] transition-colors" data-testid={`survey-review-${i}`}><Star size={15} className="inline mr-1 -mt-0.5" /> {t("Recensisci su", "Review us on")} {x.canale || "Google"}</a>)}</div>
        </div>
      )}
      {r.referral_code && (
        <div className="rounded-xl bg-[#EAF5F7] p-4 space-y-2 text-sm text-left">
          <b>{t("Porta un amico 🎁", "Bring a friend 🎁")}</b>
          <p className="text-[#47606B]">{t("Condividi il tuo codice", "Share your code")} <b data-testid="survey-referral">{r.referral_code}</b>{t(": chi prenota un evento con noi riceve un omaggio, e anche tu.", ": whoever books an event with us gets a gift, and so do you.")}</p>
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => navigator.clipboard.writeText(link).then(() => toast.success("Link copiato"))} data-testid="survey-referral-copy"><Copy size={13} className="mr-1" /> {t("Copia il link da condividere", "Copy the link to share")}</Button>
        </div>
      )}
    </div>
  );
}

export default function Survey() {
  const { token } = useParams();
  const [ev, setEv] = useState(null);
  const [err, setErr] = useState("");
  const [voti, setVoti] = useState({});
  const [commento, setCommento] = useState("");
  const [cons, setCons] = useState(true);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lang, setLang, t] = useLang();
  useEffect(() => {
    api.get(`/public/survey/${token}`).then((r) => { setEv(r.data); if (r.data.done) setRes(r.data); }).catch((e) => setErr(errMsg(e)));
  }, [token]);
  const send = async () => {
    setBusy(true);
    try {
      setRes((await api.post(`/public/survey/${token}`, { voti, commento, consiglierebbe: cons })).data);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return (
    <PublicShell testId="survey-page" lang={lang} setLang={setLang}>
      <Card>
        {err && <div className="text-[#991B1B]" data-testid="survey-error">{err}</div>}
        {ev && res && <Thanks r={res} t={t} />}
        {ev && !res && (
          <div className="space-y-5">
            <div className="text-center">
              <div className="text-xs uppercase tracking-[0.25em] text-[#34B3B0]">{t("Com'è andata?", "How did it go?")}</div>
              <h1 className="font-serif-it text-3xl font-semibold mt-1">{t(`${ev.cliente_nome}, raccontaci il tuo ${ev.evento_tipo?.toLowerCase() || "evento"}`, `${ev.cliente_nome}, tell us about your day`)}</h1>
            </div>
            {Q.map(([k, l, le]) => <div key={k} className="flex items-center justify-between gap-3 border-b border-[#EAF5F7] pb-3"><span className="text-sm font-medium">{t(l, le)}</span><Stars value={voti[k]} onChange={(n) => setVoti({ ...voti, [k]: n })} testId={`survey-${k}`} /></div>)}
            <textarea rows={3} className={inputCls} placeholder={t("Un pensiero, un ricordo, un consiglio…", "A thought, a memory, a suggestion…")} value={commento} onChange={(e) => setCommento(e.target.value)} data-testid="survey-comment" />
            <label className="flex gap-2 text-sm"><input type="checkbox" checked={cons} onChange={(e) => setCons(e.target.checked)} className="accent-[#0B6E8F]" data-testid="survey-recommend" /> {t("Ci consiglieresti ad amici e parenti", "I would recommend you to friends and family")}</label>
            <Button className="w-full rounded-full h-12 text-base" disabled={busy || Q.some(([k]) => !voti[k])} onClick={send} data-testid="survey-submit">{busy && <Loader2 size={16} className="mr-2 animate-spin" />} {t("Invia", "Send")}</Button>
          </div>
        )}
      </Card>
    </PublicShell>
  );
}
